// App.js - Configuración de rutas corregida para navegación funcional
import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { LazyMotion } from 'framer-motion';
import lazyRetry from './utils/lazyRetry';

// Importar sistema de autenticación
import { AuthProvider, useAuth } from './auth/AuthContext';
import PrivateRoute from './auth/PrivateRoute';
import LoginScreen from './auth/LoginScreen';

// Importar componentes principales
import Layout from './components/Layout';
import TaskApp from './components/TaskApp';
import CommandPalette from './components/CommandPalette';

// framer-motion en diferido (revisión 2026-10, informe 05, MEDIA-2): el bundle
// inicial solo lleva el núcleo de `m` y las features (animaciones, gestos y
// layout, domMax) llegan en un chunk aparte. El import se lanza al evaluar este
// módulo, no al montar LazyMotion, para que el chunk salga en cuanto ejecuta
// main.js. Con lazyRetry: si el chunk falta tras un deploy, recarga una vez
// (sin features, un `m.div` con initial opacity 0 se quedaría invisible).
// strict: cualquier `motion.*` dentro de LazyMotion lanza error en desarrollo
// (arrastraría todas las features al bundle inicial). Usar siempre `m.*`.
// Si el chunk falla de forma definitiva (tras los reintentos y la recarga única
// de lazyRetry), LazyMotion no captura el rechazo y los m.* se quedarían en su
// estado initial (login y pantallas nuevas con opacidad 0). Aquí se registra el
// error, se marca <html class="motion-unavailable"> (index.css muestra esos
// elementos en su estado final) y se resuelve con features vacías: la app sigue
// funcionando, sin animaciones (corrección tras QA, oleada B).
const motionFeaturesPromise = lazyRetry(() => import('./components/motionFeatures'), 'motionFeatures')
  .then((mod) => mod.default)
  .catch((error) => {
    console.error('[motion] No se pudieron cargar las animaciones, la app sigue sin ellas:', error);
    try { document.documentElement.classList.add('motion-unavailable'); } catch (e) { /* sin DOM */ }
    return {};
  });
const loadMotionFeatures = () => motionFeaturesPromise;

// Título de la pestaña por módulo/sección (se ve en la barra del navegador).
// Siempre con el sufijo de producto "- Marketing Suite".
// Cubre TODAS las secciones navegables (addons + pantallas core); cualquier
// ruta que falte aquí se ve como "Task Manager" y parece que la pestaña no cambia.
// Las etiquetas coinciden con AddonTabs / CommandPalette.
function titleForPath(pathname) {
  if (pathname.startsWith('/task/crawl')) return 'Crawl Dashboard';
  if (pathname.startsWith('/task/gads-panel')) return 'Google Ads Dashboard';
  if (pathname.startsWith('/task/changelog')) return 'Changelog';
  if (pathname.startsWith('/task/meta')) return 'Meta Ads';
  if (pathname.startsWith('/task/tiktok')) return 'TikTok Ads';
  if (pathname.startsWith('/task/seo')) return 'SEO';
  if (pathname.startsWith('/task/creatives')) return 'Creatividades';
  if (pathname.startsWith('/task/aivis')) return 'Visibilidad IA';
  if (pathname.startsWith('/task/onesearch')) return 'OneSearch';
  if (pathname.startsWith('/task/documentation')) return 'Documentación';
  if (pathname.startsWith('/task/sprint-history')) return 'Histórico de sprints';
  if (pathname.startsWith('/task/users')) return 'Usuarios';
  if (pathname.startsWith('/task/client-config')) return 'Configuración';
  if (pathname.startsWith('/task/profile')) return 'Perfil';
  if (pathname.startsWith('/task/create')) return 'Crear tarea';
  if (pathname.startsWith('/task/edit/')) return 'Editar tarea';
  if (pathname.startsWith('/task/detail/')) {
    // Referencia corta en la pestaña (SKELLO-12); los ids legacy quedan como "Tarea".
    const slug = decodeURIComponent(pathname.split('/')[3] || '');
    return /^[A-Za-z]{1,10}-\d+$/.test(slug) ? slug.toUpperCase() : 'Tarea';
  }
  return 'Tareas';
}

// Componente interno que usa useAuth
function AppContent() {
  const { token } = useAuth();
  const location = useLocation();

  // El título cambia según la sesión y la sección actual (marca siempre presente).
  // Con sesión, se antepone el NOMBRE DEL CLIENTE (tenant) para diferenciar
  // pestañas de un vistazo: p.ej. "Skello Tareas - Marketing Suite",
  // "Skello Crawl Dashboard - Marketing Suite". El nombre real llega en
  // window.__APP_CONFIG__.CLIENT_NAME (get_public_config del tenant); si aún es
  // el placeholder por defecto, se omite y queda "{sección} - Marketing Suite".
  useEffect(() => {
    if (!token) {
      document.title = 'Marketing Suite';
      return;
    }
    const section = titleForPath(location.pathname);
    const client = window.__APP_CONFIG__ && window.__APP_CONFIG__.CLIENT_NAME;
    const hasClient = client && client !== 'Task Manager' && client !== 'Default Client';
    document.title = hasClient
      ? `${client} ${section} - Marketing Suite`
      : `${section} - Marketing Suite`;
  }, [token, location.pathname]);

  return (
    <div className="app">
      {/* Paleta de comandos global (Cmd/Ctrl+K). Dentro del Router y del
          AuthProvider (para navigate + sesión); solo se activa con token. */}
      <CommandPalette />

      <Routes>
        {/* Ruta de login (incluye el flujo de recuperación de contraseña,
            que vive dentro de la propia pantalla: /login?reset={token}) */}
        <Route path="/login" element={<LoginScreen />} />

        {/* Todas las rutas que empiecen con /task van al TaskApp */}
        <Route 
          path="/task/*" 
          element={
            <PrivateRoute>
              <Layout>
                <TaskApp />
              </Layout>
            </PrivateRoute>
          } 
        />
        
        {/* Redirección de la raíz al dashboard de tareas */}
        <Route 
          path="/" 
          element={
            <PrivateRoute>
              <Navigate to="/task" replace />
            </PrivateRoute>
          } 
        />
        
        {/* Cualquier otra ruta redirige al dashboard */}
        <Route 
          path="*" 
          element={
            <PrivateRoute>
              <Navigate to="/task" replace />
            </PrivateRoute>
          } 
        />
      </Routes>
    </div>
  );
}

function App() {
  return (
    <LazyMotion features={loadMotionFeatures} strict>
      <AuthProvider>
        <Router>
          <AppContent />
        </Router>
      </AuthProvider>
    </LazyMotion>
  );
}

export default App;